/**
 * Countries a billing identity can declare.
 *
 * The list is not cosmetic: it decides the VAT. The 27 EU member states are all
 * here because an intra-EU business with a valid VAT number is invoiced under
 * the reverse charge, and a consumer is invoiced at their own country's rate —
 * neither can happen if their country cannot be picked. The handful of non-EU
 * entries are the ones the platform actually sees.
 *
 * Names come from `Intl.DisplayNames`, so they follow the displayed language
 * rather than a translated list that would drift.
 */
export const EU_COUNTRY_CODES = [
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI',
  'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT',
  'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK',
] as const

const NON_EU_COUNTRY_CODES = ['CH', 'GB', 'US', 'CA'] as const

export const BILLING_COUNTRY_CODES: readonly string[] = [
  ...EU_COUNTRY_CODES,
  ...NON_EU_COUNTRY_CODES,
]

export interface CountryOption {
  code: string
  name: string
}

/**
 * Build the country list, named in `locale` and sorted for that language.
 *
 * @param locale - Language the app is displaying.
 * @returns Country options, alphabetical by localized name.
 */
export function billingCountries(locale: string): CountryOption[] {
  const names = new Intl.DisplayNames([locale], { type: 'region' })
  return BILLING_COUNTRY_CODES.map((code) => ({ code, name: names.of(code) ?? code })).sort(
    (a, b) => a.name.localeCompare(b.name, locale)
  )
}

/** Whether a country is in the EU — where a VAT number changes the invoice. */
export function isEuCountry(code: string | undefined): boolean {
  return !!code && (EU_COUNTRY_CODES as readonly string[]).includes(code.toUpperCase())
}
